import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import type { Simplify } from 'type-fest';
import BulkSelectionBanner from '../../../../../../../../generic/BulkSelectionBanner';
import FallbackImage from '../../../../../../../../generic/FallbackImage';
import List from '../../../../../../../../generic/List';
import ProjectRow from './ProjectRow/ProjectRow';
import type { AddedProjectIndex } from '../../../../types';
import { hasFiltersApplied } from '../../../../utils';
import AddProjectsCTA from '../../../../../../../shared/CTAs/AddProjectsCTA';
import type { UseSelectProjects } from '../../../../../hooks/useSelectProjects';
import type { Project, ProjectsFilter } from '../../../../../../../../../../shared/projectsV2';
import type { AddedProject } from '../../../../../../../../../shared/branches/types';
import type { SelectOption } from '../../../../../../../../../types';
import type { TeamSelectOption } from '../../../../../../../../shared/teams/hooks/useTeamsSelectOptions';

type ProjectBodyUseSelectProps =
  | 'areAllProjectsSelected'
  | 'areAllProjectsOnPageSelected'
  | 'onSelectAllAcrossAllPages'
  | 'isProjectSelected'
  | 'onToggleIsProjectSelected'
  | 'onClearSelectAll';

type Props = Simplify<Pick<UseSelectProjects, ProjectBodyUseSelectProps> & {
  totalProjectCount: number;
  areProjectsLoading: boolean;
  projects: Project[];
  projectErrors: string[] | null;
  addedProjects: AddedProjectIndex;
  pageCount: number;
  filters: ProjectsFilter;
  onToggleIsProjectSelected: (projectLocator: string) => void;
  isProjectSelected: (projectLocator: string) => boolean;
  onUpdateAddedProject: (project: AddedProject) => void;
  onAddProjects: (projects: Project[]) => void;
  onRemoveProjects: (projects: Project[]) => void;
  onLabelClick: (selected: SelectOption[]) => void;
  selectedLabels: SelectOption[];
  onTeamClick: (selected: TeamSelectOption[]) => void;
  selectedTeams: TeamSelectOption[];
}>;

const ProjectsTableBody: VoidFunctionComponent<Props> = ({
  areAllProjectsOnPageSelected,
  areAllProjectsSelected,
  totalProjectCount,
  onSelectAllAcrossAllPages,
  onClearSelectAll,
  areProjectsLoading,
  projects,
  projectErrors,
  addedProjects,
  pageCount,
  filters,
  onToggleIsProjectSelected,
  isProjectSelected,
  onUpdateAddedProject,
  onAddProjects,
  onRemoveProjects,
  onLabelClick,
  selectedLabels,
  onTeamClick,
  selectedTeams,
}) => {
  if (areProjectsLoading) {
    return (
      <List.RowContainer>
        {_.times(pageCount, (num) => (
          <List.RowLoading key={`project-row-loading-${num}`} />
        ))}
      </List.RowContainer>
    );
  }

  if (!_.isEmpty(projectErrors)) {
    return (
      <FallbackImage src="error-plug" alt="Network error" hasPadding>
        <div>Oops! There was an error.</div>
        <div>Please try again in a bit.</div>
      </FallbackImage>
    );
  }

  if (projects.length === 0) {
    // if no projects are found, load one of two fallback states depending on if filters are applied
    if (hasFiltersApplied(filters)) {
      return (
        <FallbackImage src="no-results" alt="No projects found" hasPadding>
          We couldn&apos;t find any results.
        </FallbackImage>
      );
    }
    return <AddProjectsCTA />;
  }

  // if any projects are found, render the table and selection banner
  return (
    <>
      {(areAllProjectsOnPageSelected || areAllProjectsSelected) && (
        <BulkSelectionBanner
          itemsNoun="project"
          pageSize={projects.length < pageCount ? projects.length : pageCount}
          totalItemsCount={totalProjectCount}
          areAllItemsSelected={areAllProjectsSelected}
          sectionName="Projects"
          onSelectAll={onSelectAllAcrossAllPages}
          onClearSelection={onClearSelectAll}
        />
      )}
      {!areProjectsLoading && projects.length === 0 && (
        <FallbackImage
          src="no-results"
          alt="No projects found"
          hasPadding
        >
          We couldn&apos;t find any results.
        </FallbackImage>
      )}
      {areProjectsLoading ? (
        <List.RowContainer>
          {_.times(pageCount, (num) => (
            <List.RowLoading key={`project-row-loading-${num}`} />
          ))}
        </List.RowContainer>
      ) : (
        <>
          {projects.map((project) => (
            <ProjectRow
              key={project.id}
              project={project}
              addedProject={addedProjects[project.id]}
              onToggleIsProjectSelected={onToggleIsProjectSelected}
              isChecked={isProjectSelected(project.id)}
              onUpdateAddedProject={onUpdateAddedProject}
              onAddProjects={onAddProjects}
              onRemoveProjects={onRemoveProjects}
              onLabelClick={onLabelClick}
              selectedLabels={selectedLabels}
              onTeamClick={onTeamClick}
              selectedTeams={selectedTeams}
            />
          ))}
        </>
      )}
    </>
  );
};

export default ProjectsTableBody;
